<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, nextTick } from 'vue'
import type { 
  JSearchAddrProps, 
  PostcodeData, 
  AddressResult 
} from '@/types/search-addr.types'

/**
 * JSearchAddr 컴포넌트
 * 
 * Daum Postcode API를 활용한 주소 검색 컴포넌트입니다.
 * 모달 형태로 동작하며, 우편번호와 주소를 검색할 수 있습니다.
 * 
 * @emits close - 모달 닫힘 요청
 * @emits complete - 주소 선택 완료 (간소화된 데이터)
 * @emits complete-raw - 주소 선택 완료 (원본 데이터)
 * @emits search - 검색 시작
 * @emits error - 에러 발생
 */

const props = withDefaults(defineProps<JSearchAddrProps>(), {
  isOpen: false,
  title: '주소 검색',
  width: '600px',
  height: '600px',
  closeOnBackdrop: true,
  closeOnEsc: true,
  defaultLanguage: 'K',
  theme: 'default',
  autoComplete: true
})

const emit = defineEmits<{
  close: []
  complete: [result: AddressResult]
  'complete-raw': [data: PostcodeData]
  search: []
  error: [error: Error]
}>()

// Refs
const modalRef = ref<HTMLElement | null>(null)
const postcodeLayerRef = ref<HTMLElement | null>(null)
const isScriptLoaded = ref(false)
const isLoading = ref(false)
const postcodeInstance = ref<any>(null)

/**
 * Daum Postcode 스크립트 로드
 */
const loadPostcodeScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.daum && window.daum.Postcode) {
      isScriptLoaded.value = true
      resolve()
      return
    }

    const existingScript = document.getElementById('daum-postcode-script')
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        isScriptLoaded.value = true
        resolve()
      })
      return
    }

    const script = document.createElement('script')
    script.id = 'daum-postcode-script'
    script.src = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js'
    script.async = true

    script.onload = () => {
      isScriptLoaded.value = true
      resolve()
    }

    script.onerror = () => {
      const error = new Error('Failed to load Daum Postcode script')
      emit('error', error)
      reject(error)
    }

    document.head.appendChild(script)
  })
}

/**
 * 주소 데이터 변환 (PostcodeData → AddressResult)
 */
const transformAddressData = (data: PostcodeData): AddressResult => {
  let mainAddr = ''
  let extraAddr = ''

  // 언어에 따라 주소 선택
  if (data.userLanguageType === 'K') {
    if (data.userSelectedType === 'R') {
      mainAddr = data.roadAddress
      
      // 건물명 등 추가 정보
      if (data.bname !== '') {
        extraAddr += data.bname
      }
      if (data.buildingName !== '') {
        extraAddr += (extraAddr !== '' ? ', ' + data.buildingName : data.buildingName)
      }
    } else {
      mainAddr = data.jibunAddress
    }
  } else {
    if (data.userSelectedType === 'R') {
      mainAddr = data.roadAddressEnglish
    } else {
      mainAddr = data.jibunAddressEnglish
    }
  }

  return {
    zonecode: data.zonecode,
    address: mainAddr,
    addressDetail: extraAddr || undefined,
    addressType: data.userSelectedType,
    languageType: data.userLanguageType,
    sido: data.sido,
    sigungu: data.sigungu,
    bname: data.bname
  }
}

/**
 * Postcode 초기화 및 embed
 */
const initPostcode = async () => {
  if (!postcodeLayerRef.value) {
    console.warn('postcodeLayerRef is not available')
    return
  }

  try {
    isLoading.value = true

    // 스크립트 로드
    await loadPostcodeScript()

    // DOM 준비 대기
    await nextTick()

    // Postcode 인스턴스 생성 및 embed
    postcodeInstance.value = new window.daum.Postcode({
      oncomplete: (data: PostcodeData) => {
        const result = transformAddressData(data)
        emit('complete', result)
        emit('complete-raw', data)
        closeModal()
      },
      onclose: () => {
        // 사용자가 X 버튼으로 닫을 경우
        if (props.isOpen) {
          closeModal()
        }
      },
      onsearch: () => {
        emit('search')
      },
      width: '100%',
      height: '100%'
    })

    // 레이어에 embed
    if (postcodeLayerRef.value) {
      postcodeInstance.value.embed(postcodeLayerRef.value)
      console.log('Daum Postcode embedded successfully')
    }
    
    isLoading.value = false
  } catch (error) {
    isLoading.value = false
    console.error('Failed to initialize Postcode:', error)
    emit('error', error instanceof Error ? error : new Error('Unknown error'))
  }
}

/**
 * 모달 열기
 */
const openModal = async () => {
  // body 스크롤 잠금
  document.body.style.overflow = 'hidden'
  
  // DOM이 완전히 렌더링될 때까지 대기
  await nextTick()
  
  // 약간의 추가 지연 (모달 애니메이션 고려)
  setTimeout(() => {
    initPostcode()
  }, 100)
}

/**
 * 모달 닫기
 */
const closeModal = () => {
  // body 스크롤 복원
  document.body.style.overflow = ''
  
  emit('close')
}

/**
 * 배경 클릭 핸들러
 */
const handleBackdropClick = (event: MouseEvent) => {
  if (props.closeOnBackdrop && event.target === event.currentTarget) {
    closeModal()
  }
}

/**
 * ESC 키 핸들러
 */
const handleEscKey = (event: KeyboardEvent) => {
  if (props.closeOnEsc && event.key === 'Escape' && props.isOpen) {
    closeModal()
  }
}

// Watch isOpen
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      openModal()
    } else {
      document.body.style.overflow = ''
    }
  }
)

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscKey)
  
  if (props.isOpen) {
    openModal()
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscKey)
  document.body.style.overflow = ''
})

// Expose
defineExpose({
  close: closeModal
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isOpen"
        ref="modalRef"
        class="search-addr-overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
        @click="handleBackdropClick"
      >
        <div
          class="search-addr-modal"
          :style="{ width, height }"
          @click.stop
        >
          <!-- Header -->
          <div class="search-addr-header">
            <h2 class="search-addr-title">{{ title }}</h2>
            <button
              type="button"
              class="search-addr-close"
              aria-label="닫기"
              @click="closeModal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="search-addr-body">
            <!-- 로딩 -->
            <div v-if="isLoading" class="search-addr-loading">
              <div class="loading-spinner"></div>
              <p>주소 검색을 불러오는 중...</p>
            </div>

            <!-- Postcode Layer -->
            <div
              ref="postcodeLayerRef"
              class="postcode-layer"
              :class="{ 'is-loading': isLoading }"
            ></div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Overlay */
.search-addr-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

/* Modal */
.search-addr-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
              0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
}

/* Header */
.search-addr-header {
  position: relative;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.search-addr-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  padding-right: 40px;
}

.search-addr-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  padding: 0;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.search-addr-close:hover {
  background-color: #f3f4f6;
  color: #111827;
}

.search-addr-close:focus {
  outline: 2px solid #3b82f6;
  outline-offset: 2px;
}

.search-addr-close:active {
  background-color: #e5e7eb;
}

/* Body */
.search-addr-body {
  flex: 1;
  min-height: 0;
  position: relative;
}

.postcode-layer {
  width: 100%;
  height: 100%;
  position: relative;
}

.postcode-layer.is-loading {
  opacity: 0;
  pointer-events: none;
}

/* Loading */
.search-addr-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f9fafb;
  z-index: 10;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.search-addr-loading p {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

/* Animation */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active .search-addr-modal,
.modal-fade-leave-active .search-addr-modal {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .search-addr-modal,
.modal-fade-leave-to .search-addr-modal {
  transform: scale(0.95);
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .search-addr-overlay {
    padding: 0;
  }

  .search-addr-modal {
    width: 100vw !important;
    height: 100vh !important;
    max-width: 100vw;
    max-height: 100vh;
    border-radius: 0;
  }

  .search-addr-header {
    padding: 16px 20px;
  }

  .search-addr-title {
    font-size: 18px;
  }
}

/* Dark mode */
@media (prefers-color-scheme: dark) {
  .search-addr-modal {
    background: #1f2937;
  }

  .search-addr-header {
    border-color: #374151;
  }

  .search-addr-title {
    color: #f9fafb;
  }

  .search-addr-close {
    color: #9ca3af;
  }

  .search-addr-close:hover {
    background-color: #374151;
    color: #f9fafb;
  }

  .search-addr-loading {
    background: #111827;
  }

  .search-addr-loading p {
    color: #9ca3af;
  }
}
</style>

